import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Benefits|Associated Credentials|';
const columnHeadingStart = 'Column heading for: ';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Benefit,
	portal: 'SP',
};

export type SPBenefitsAssociatedCredentialsLocNames =
	| `SPBenefitsAssociatedCredentialsTitlePage`
	| `SPBenefitsAssociatedCredentialsButtonCancel`
	| `SPBenefitsAssociatedCredentialsButtonAddSelected`
	| `SPBenefitsAssociatedCredentialsButtonAddCredentials`
	| `SPBenefitsAssociatedCredentialsLabelGroup`
	| `SPBenefitsAssociatedCredentialsLabelFilterByGroup`
	| `SPBenefitsAssociatedCredentialsLabelName`
	| `SPBenefitsAssociatedCredentialsLabelAbbreviation`
	| `SPBenefitsAssociatedCredentialsLabelStartDate`
	| `SPBenefitsAssociatedCredentialsLabelEndDate`
	| 'SPBenefitsAssociatedCredentialsButtonEditEndDate'
	| 'SPBenefitsAssociatedCredentialsTitleModifyEndDate'
	| 'SPBenefitsAssociatedCredentialsLabelGrantEndDate'
	| 'SPBenefitsAssociatedCredentialsButtonSaveEndDate'
	| 'SPBenefitsAssociatedCredentialsButtonCancelEndDate'
	| 'SPBenefitsAssociatedCredentialsLabelRemoveEndDate'
	| 'SPBenefitsAssociatedCredentialsFormErrorGrantEndDateRequired'
	| 'SPBenefitsAssociatedCredentialsFormErrorGrantEndDateMin'
	| 'SPBenefitsAssociatedCredentialsFormErrorGrantEndDateParse'
	| 'SPBenefitsAssociatedCredentialsSuccessModifyEndDate';

export const spBenefitsAssociatedCredentialsLocs: LocDefs<SPBenefitsAssociatedCredentialsLocNames> =
	{
		SPBenefitsAssociatedCredentialsTitlePage: {
			defaultValue: 'Associated Credentials',
			description: 'Page Title',
			displayPath: `${pathStart}Page Title`,
			displayType: 'Title',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.Title.PageTitle',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsButtonCancel: {
			defaultValue: 'Cancel',
			description: 'Cancel button.',
			displayPath: `${pathStart}Cancel`,
			displayType: 'Button',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.Button.Cancel',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsButtonAddSelected: {
			defaultValue: 'Add Selected',
			description: 'Add Selected button.',
			displayPath: `${pathStart}Add Selected`,
			displayType: 'Button',
			legacyName:
				'Benefit/Catalogs.Benefits.AssociatedCredentials.Button.AddSelectedCredentials',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsButtonAddCredentials: {
			defaultValue: 'Add Credentials',
			description: 'Add Credentials button.',
			displayPath: `${pathStart}Add Credentials`,
			displayType: 'Button',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.Button.AddCredentials',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsLabelGroup: {
			defaultValue: 'Group',
			description: `${columnHeadingStart}Group`,
			displayPath: `${pathStart}Group`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.ColumnHeading.Group',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsLabelFilterByGroup: {
			defaultValue: 'Filter by Group',
			description: `Label for: Filter by Group`,
			displayPath: `${pathStart}Filter by Group`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.FieldLabel.FilterGroup',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsLabelName: {
			defaultValue: 'Name',
			description: `${columnHeadingStart}Name`,
			displayPath: `${pathStart}Name`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.ColumnHeading.Name',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsLabelAbbreviation: {
			defaultValue: 'Abbreviation',
			description: `${columnHeadingStart}Abbreviation`,
			displayPath: `${pathStart}Abbreviation`,
			displayType: 'Label',
			legacyName:
				'Benefit/Catalogs.Benefits.AssociatedCredentials.ColumnHeading.Abbreviation',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsLabelStartDate: {
			defaultValue: 'Start Date',
			description: `${columnHeadingStart}Start Date`,
			displayPath: `${pathStart}Start Date`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.ColumnHeading.StartDate',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsLabelEndDate: {
			defaultValue: 'End Date',
			description: `${columnHeadingStart}End Date`,
			displayPath: `${pathStart}End Date`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.ColumnHeading.EndDate',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsButtonEditEndDate: {
			defaultValue: 'Edit End Date',
			description: 'Edit End Date button.',
			displayPath: `${pathStart}Edit End Date`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsTitleModifyEndDate: {
			defaultValue: 'Modify Associated Credential End Date(s)',
			description: 'Title for: Modify Associated Credential End Date(s)',
			displayPath: `${pathStart}End Date|Title`,
			displayType: 'Title',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsLabelGrantEndDate: {
			defaultValue: 'End Date',
			description: 'Label for: End Date',
			displayPath: `${pathStart}End Date|Field Label`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsButtonSaveEndDate: {
			defaultValue: 'Save End Date Changes',
			description: 'Save end date button.',
			displayPath: `${pathStart}End Date|Button Save`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsButtonCancelEndDate: {
			defaultValue: 'Cancel',
			description: 'Cancel button.',
			displayPath: `${pathStart}End Date|Button Cancel`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsLabelRemoveEndDate: {
			defaultValue: 'Remove End Date',
			description: 'Label for: Remove End Date',
			displayPath: `${pathStart}End Date|Label Remove`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormErrorGrantEndDateRequired: {
			defaultValue: 'End date is required',
			description: 'Error saying that the end date is required',
			displayPath: `${pathStart}End Date Required`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormErrorGrantEndDateMin: {
			defaultValue: 'End date must be in the future',
			description: 'Error saying that the end date must be in the future',
			displayPath: `${pathStart}End Date Min`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormErrorGrantEndDateParse: {
			defaultValue: 'Invalid date',
			description: 'Error saying that the end date is invalid',
			displayPath: `${pathStart}End Date Parse`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsSuccessModifyEndDate: {
			defaultValue: 'End date(s) modified successfully',
			description: 'Success message for: End date(s) modified successfully',
			displayPath: `${pathStart}End Date Modified Success`,
			displayType: 'Notification',
			...sharedProperties,
		},
	};

export type SPBenefitsAssociatedCredentialsLocType =
	LocMap<SPBenefitsAssociatedCredentialsLocNames>;
